import React, { useCallback, useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaGallery } from "../media/MediaGallery";
import { MediaUploader } from "../media/MediaUploader";
import { MediaProgressIndicator } from "../media/MediaProgressIndicator";
import { usePreviewMediaUpload } from "@/hooks/usePreviewMediaUpload";
import { supabase } from "@/integrations/supabase/client";
import { Story } from "./types";
import { Media } from "../media/types";
import DatePickerField from "../memorial-creation/DatePickerField";
import { Loader2, Sparkles, AlertCircle, Save } from "lucide-react";
import { StoryPrompt, storyPromptCategories } from "./prompts/storyPrompts";
import { handleStoryMediaRemoval } from "./hooks/useStoryActions/utils/mediaDeleteHandlers";
import { showSuccessToast, showErrorToast } from "@/utils/toast";
import { compressForUpload } from "@/utils/imageCompression";
import { logger } from "@/lib/logger";

// Videos are hosted on Cloudflare Stream (never the 50MB image bucket); cap the
// upload size so a huge clip fails fast with a clear message instead of hanging.
const MAX_VIDEO_MB = 200;
const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;

/**
 * POST a file with upload-progress reporting. `fetch` can't report upload
 * progress, so videos (the slow transfer) use XHR to drive a real progress bar.
 */
const postWithProgress = (
  url: string,
  body: FormData,
  onProgress: (fraction: number) => void
): Promise<{ ok: boolean; status: number }> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status });
    xhr.onerror = () => reject(new Error('Network error during video upload'));
    xhr.send(body);
  });

/**
 * Returns a copy of `file` whose name extension matches its MIME type, so the
 * stored object's extension lines up with its content (e.g. compressed WebP
 * output gets a `.webp` name). Returns the file unchanged for types we don't
 * remap — e.g. when compression fell back to the original file.
 */
const matchExtensionToType = (file: File, originalName: string): File => {
  const extByType: Record<string, string> = {
    "image/webp": "webp",
    "image/jpeg": "jpg",
    "image/png": "png",
  };
  const ext = extByType[file.type];
  if (!ext) return file;
  const base = originalName.replace(/\.[^./\\]+$/, "");
  return new File([file], `${base}.${ext}`, { type: file.type });
};

interface StoryFormProps {
  story: Partial<Story>;
  onUpdate: (story: Partial<Story>) => void;
  onSave: () => Promise<boolean>;
  onMediaUpload: (photos: string[]) => void;
  onMediaRemove: (index: number) => void;
  onFilePreview?: (files: File[]) => void;
  buttonText?: string;
  isSaving?: boolean;
  selectedPrompt?: StoryPrompt | null;
  onBack?: () => void;
  previewFiles?: File[];
  usePreviewMode?: boolean;
  onSaveSuccess?: () => void;
}

export const StoryForm = ({
  story,
  onUpdate,
  onSave,
  onMediaUpload,
  onMediaRemove,
  onFilePreview,
  buttonText = "Save Story",
  isSaving = false,
  selectedPrompt,
  onBack,
  previewFiles = [],
  usePreviewMode = false,
  onSaveSuccess
}: StoryFormProps) => {
  const [previewMedia, setPreviewMedia] = useState<Media[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Progress for the post-selection processing step (compress images / upload videos).
  const [processing, setProcessing] = useState<{
    phase: 'compressing' | 'uploading';
    current: number;
    total: number;
    fileType: 'image' | 'video';
    percent: number;
  } | null>(null);
  const { cleanupPreviewMedia, uploadFileForPreview } = usePreviewMediaUpload();
  const previewMediaRef = useRef<Media[]>(previewMedia);
  const wasSavedRef = useRef(false);

  // Keep ref in sync with state for cleanup
  useEffect(() => {
    previewMediaRef.current = previewMedia;
  }, [previewMedia]);

  // Determine if we should use preview mode - now forced for all editing
  const isNewStory = !story.id;
  const shouldUsePreviewMode = usePreviewMode || isNewStory;

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = previewMedia.length > 0 ||
                      (story.media && story.media.some(m => m.id.startsWith('preview-')));
    setHasUnsavedChanges(hasChanges);
  }, [previewMedia, story.media]);

  // Process preview files when they change
  const storyRef = useRef(story);
  useEffect(() => {
    storyRef.current = story;
  }, [story]);

  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Process preview files: images use local blob URLs, videos upload to Cloudflare immediately
  const processedFilesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!shouldUsePreviewMode || !story.memorial_id) return;

    let cancelled = false;
    const blobUrls: string[] = [];

    const processFiles = async () => {
      const currentStory = storyRef.current;
      const currentPreview = [...previewMediaRef.current];

      // Only the newly-selected files still need processing.
      const newFiles = previewFiles.filter(
        (file) => !processedFilesRef.current.has(`${file.name}-${file.size}`)
      );
      if (newFiles.length === 0) return;
      const total = newFiles.length;

      for (let i = 0; i < total; i++) {
        const file = newFiles[i];
        const fileKey = `${file.name}-${file.size}`;
        processedFilesRef.current.add(fileKey);

        const isVideo = file.type.startsWith('video/');
        if (!cancelled) {
          setProcessing({
            phase: isVideo ? 'uploading' : 'compressing',
            current: i + 1,
            total,
            fileType: isVideo ? 'video' : 'image',
            percent: Math.round((i / total) * 100),
          });
        }

        if (isVideo) {
          // Videos are hosted on Cloudflare Stream and referenced by stream_id —
          // they never go to the storage bucket. Upload here so the heavy transfer
          // happens once, up front, and surface any failure instead of silently
          // dropping the file or pushing a blob: URL that would later abort the save.
          if (file.size > MAX_VIDEO_BYTES) {
            logger.error('Video exceeds max upload size', undefined, { fileName: file.name, size: file.size });
            showErrorToast(`"${file.name}" is a bit too large to add — videos need to be under ${MAX_VIDEO_MB}MB.`);
            continue;
          }
          try {
            const { data: streamData, error: streamError } = await supabase.functions.invoke('create-stream-upload', {
              body: { maxDurationSeconds: 600 },
            });
            if (cancelled) return;
            if (streamError || !streamData?.uploadURL) {
              throw new Error(streamError?.message || 'Could not start the video upload');
            }

            const formData = new FormData();
            formData.append('file', file);
            const uploadResponse = await postWithProgress(streamData.uploadURL, formData, (fraction) => {
              if (!cancelled) {
                setProcessing((prev) =>
                  prev ? { ...prev, percent: Math.round(((i + fraction) / total) * 100) } : prev
                );
              }
            });
            if (cancelled) return;
            if (!uploadResponse.ok) {
              throw new Error(`Video upload failed (status ${uploadResponse.status})`);
            }

            const thumbnailUrl = `https://videodelivery.net/${streamData.uid}/thumbnails/thumbnail.jpg`;
            currentPreview.push({
              id: `preview-${fileKey}`,
              url: thumbnailUrl,
              thumbnail_url: thumbnailUrl,
              media_type: 'video',
              memorial_id: story.memorial_id,
              stream_id: streamData.uid,
              temp_file: file,
            });
          } catch (error) {
            logger.error('Video upload to Cloudflare Stream failed', error instanceof Error ? error : undefined, { fileName: file.name });
            showErrorToast(`We couldn't add "${file.name}" just now. Please try that video again.`);
          }
        } else if (file.type.startsWith('image/')) {
          // Images: compress one at a time before preview/upload. compressForUpload
          // does a single lightweight encode (and skips already-small images), which
          // both shrinks large iPhone photos below the storage limit and keeps peak
          // memory low enough to process a bulk selection without mobile Safari
          // killing the tab. Falls back to the original on failure, so it never blocks.
          const compressed = await compressForUpload(file);
          if (cancelled) return;
          const uploadFile = matchExtensionToType(compressed, file.name);
          const blobUrl = URL.createObjectURL(uploadFile);
          blobUrls.push(blobUrl);
          currentPreview.push({
            id: `preview-${fileKey}`,
            url: blobUrl,
            media_type: 'image',
            memorial_id: story.memorial_id,
            temp_file: uploadFile,
          });
        } else {
          // Audio: local blob URL preview, upload the raw file
          const blobUrl = URL.createObjectURL(file);
          blobUrls.push(blobUrl);
          currentPreview.push({
            id: `preview-${fileKey}`,
            url: blobUrl,
            media_type: 'audio',
            memorial_id: story.memorial_id,
            temp_file: file,
          });
        }

        // Reveal each item as it finishes so a large batch never looks frozen.
        if (!cancelled) setPreviewMedia([...currentPreview]);
      }

      if (!cancelled) {
        setPreviewMedia(currentPreview);
        const existingMedia = currentStory.media?.filter(m => !m.id.startsWith('preview-')) || [];
        onUpdateRef.current({ ...currentStory, media: [...existingMedia, ...currentPreview] });
      }
      setProcessing(null);
    };

    processFiles();

    return () => {
      cancelled = true;
      setProcessing(null);
      blobUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewFiles, story.memorial_id, shouldUsePreviewMode]);

  // On unmount without saving, delete orphaned Cloudflare Stream videos
  useEffect(() => {
    return () => {
      if (wasSavedRef.current) return; // Story was saved, videos are committed

      const orphanedStreamIds = previewMediaRef.current
        .filter(m => m.stream_id)
        .map(m => m.stream_id!);

      if (orphanedStreamIds.length > 0) {
        console.log('[StoryForm] Cleaning up orphaned Cloudflare Stream videos:', orphanedStreamIds);
        supabase.functions.invoke('delete-stream-video', {
          body: { streamIds: orphanedStreamIds },
        }).then(({ data }) => {
          console.log('[StoryForm] Cleanup result:', data);
        }).catch((err) => {
          console.error('[StoryForm] Cleanup failed:', err);
        });
      }
    };
  }, []);

  const handleFilePreview = useCallback((files: File[]) => {
    if (onFilePreview) {
      onFilePreview(files);
    }
  }, [onFilePreview]);

  const handleMediaRemove = useCallback(async (index: number) => {

    const allMedia = story.media || [];
    const mediaToRemove = allMedia[index];

    if (!mediaToRemove) return;

    // If it's preview media, handle cleanup
    if (mediaToRemove.id.startsWith('preview-')) {
      await cleanupPreviewMedia([mediaToRemove]);
      const updatedPreviewMedia = previewMedia.filter(m => m.id !== mediaToRemove.id);
      setPreviewMedia(updatedPreviewMedia);

      const updatedMedia = allMedia.filter((_, i) => i !== index);
      onUpdate({ ...story, media: updatedMedia });

      // Also update preview files if callback exists
      if (onFilePreview) {
        const previewIndex = previewMedia.findIndex(m => m.id === mediaToRemove.id);
        if (previewIndex >= 0) {
          const updatedFiles = previewFiles.filter((_, i) => i !== previewIndex);
          onFilePreview(updatedFiles);
        }
      }

      showSuccessToast("File removed from preview");
      return;
    }

    // For existing media, use the existing removal logic
    const { success, updatedMedia, error } = await handleStoryMediaRemoval(allMedia, index);

    if (success) {
      onUpdate({ ...story, media: updatedMedia });
      onMediaRemove(index);
      showSuccessToast("Media removed successfully");
    } else {
      console.error('[StoryForm] Failed to remove media:', error);
      showErrorToast("Failed to remove media. Please try again.");
    }
  }, [story, onUpdate, onMediaRemove, previewMedia, previewFiles, onFilePreview, cleanupPreviewMedia]);

  const handleDateChange = useCallback((dateString: string | undefined) => {
    onUpdate({ ...story, story_date: dateString });
  }, [story, onUpdate]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ ...story, title: e.target.value });
  }, [story, onUpdate]);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate({ ...story, content: e.target.value });
  }, [story, onUpdate]);

  const handleCategoryChange = useCallback((value: string) => {
    const categoryValue = value === 'other' ? null : value;
    onUpdate({ ...story, category: categoryValue });
  }, [story, onUpdate]);

  const handleSaveClick = useCallback(async () => {

    const saveResult = await onSave();

    // If save was successful, mark as saved and call success callback
    if (saveResult) {
      wasSavedRef.current = true;
      if (onSaveSuccess) onSaveSuccess();
    }
  }, [onSave, onSaveSuccess]);

  const getCurrentCategoryValue = () => {
    if (!story.category) return 'other';
    return story.category;
  };

  const getCurrentCategoryName = () => {
    if (!story.category) return 'Other Stories';
    const category = storyPromptCategories.find(cat => cat.id === story.category);
    return category ? category.name : 'Other Stories';
  };

  // For display: show all media (existing + preview) when using preview mode
  const displayMedia = story.media || [];

  return (
    <div className="space-y-4">
      {/* Selected Prompt Indicator */}
      {selectedPrompt && (
        <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-brand-primary" />
              <span className="text-sm font-medium text-brand-text-primary">
                Using prompt: {selectedPrompt.title}
              </span>
            </div>
            {onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="text-xs"
              >
                Change prompt
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && !isNewStory && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Unsaved Changes</p>
              <p>You have uploaded new media that hasn't been saved yet. Click "Update Story" to save your changes, or they will be lost if you close this dialog.</p>
            </div>
          </div>
        </div>
      )}

      {/* Story Form Fields */}
      <Input
        placeholder="Story Title"
        value={story.title || ''}
        onChange={handleTitleChange}
      />

      {/* Category Selection */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Category</label>
        <Select value={getCurrentCategoryValue()} onValueChange={handleCategoryChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select a category">
              {getCurrentCategoryName()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {storyPromptCategories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.icon} {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DatePickerField
        id="story_date"
        label="Story Date"
        value={story.story_date}
        onChange={handleDateChange}
        placeholder="Select story date"
      />
      <Textarea
        placeholder="Write your story here..."
        value={story.content || ''}
        onChange={handleContentChange}
        className="min-h-[200px]"
      />

      {/* Media Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Media</h3>
          {shouldUsePreviewMode && displayMedia.some(m => m.id.startsWith('preview-')) && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
              <Save className="h-3 w-3 mr-1" />
              {displayMedia.filter(m => m.id.startsWith('preview-')).length} unsaved
            </span>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {story.memorial_id ? (
            <MediaUploader
              memorialId={story.memorial_id}
              storyId={shouldUsePreviewMode ? undefined : story.id}
              onUpload={() => {}} // Let preview system handle this
              onFilePreview={handleFilePreview}
              previewMode={shouldUsePreviewMode}
              previewFiles={previewFiles}
              multiple={true}
              accept="image/*,video/*"
              className="min-h-[150px]"
            />
          ) : (
            <div className="min-h-[150px] flex items-center justify-center bg-gray-100 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mx-auto mb-2"></div>
                <p className="text-sm text-brand-secondary">Loading media upload...</p>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-4">
            <MediaGallery
              media={displayMedia}
              onRemove={handleMediaRemove}
              isEditing={true}
            />
          </div>

          <MediaProgressIndicator
            showProgress={processing !== null}
            uploadPhase={processing?.phase ?? 'idle'}
            currentFile={processing?.current}
            totalFiles={processing?.total}
            fileType={processing?.fileType}
            progressPercentage={processing?.percent}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        {onBack && (
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isSaving}
            className="flex-1"
          >
            Back
          </Button>
        )}
        <Button
          onClick={handleSaveClick}
          disabled={isSaving || !story.title || !story.content}
          className="flex-1"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              {buttonText}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
